import type { DocRetrieveResponse } from '../../openapi/types';
import { SSE_TYPE } from '../../hooks';
import { QUERY_STATE } from '../../constants';
import { AppAction } from '../types';
import { ResourceQueryResult, PaginatedResource } from './types';
import {
  RESOURCE_LOAD_START,
  RESOURCE_LOAD_SUCCESS,
  RESOURCE_LOAD_FAILURE,
  RESOURCE_INVALIDATE,
  INVALIDATE_CACHE,
  RESOURCE_PATCH_SSE,
} from './actions';

function getInitialState() {
  return {} as Record<string, ResourceQueryResult>;
}

function emptyPaginatedResource(url: string): ResourceQueryResult<PaginatedResource> {
  return {
    queryState: QUERY_STATE.EMPTY,
    data: {
      next: url,
      results: [],
    },
    fetchedAt: 0,
  };
}

export function resourceReducer(
  state = getInitialState(),
  action: AppAction,
): Record<string, ResourceQueryResult> {
  switch (action.type) {
    case INVALIDATE_CACHE: {
      return getInitialState();
    }

    case RESOURCE_LOAD_START: {
      const { url, appendTo } = action.meta;
      const targetUrl = appendTo ?? url;
      const oldResource =
        state[targetUrl] ?? (appendTo ? emptyPaginatedResource(appendTo) : { data: null });
      const resource = { ...oldResource, queryState: QUERY_STATE.WAITING };
      return {
        ...state,
        [targetUrl]: resource,
      };
    }

    case RESOURCE_LOAD_SUCCESS: {
      const { url, appendTo } = action.meta;
      const targetUrl = appendTo ?? url;
      const { data, fetchedAt } = action.payload;
      if (appendTo && url !== appendTo) {
        const oldResource = state[targetUrl] as ResourceQueryResult<PaginatedResource>;
        const newData = data as PaginatedResource;
        return {
          ...state,
          [targetUrl]: {
            ...oldResource,
            queryState: QUERY_STATE.SUCCESS,
            data: {
              ...oldResource.data,
              ...newData,
              results: [
                ...((oldResource.data as PaginatedResource)?.results ?? []),
                ...newData.results,
              ],
            },
            fetchedAt,
          },
        };
      }
      return {
        ...state,
        [targetUrl]: { queryState: QUERY_STATE.SUCCESS, data, fetchedAt },
      };
    }

    case RESOURCE_LOAD_FAILURE: {
      const { url } = action.meta;
      const { data, fetchedAt } = action.payload;
      const resource = { ...state[url], queryState: QUERY_STATE.FAIL, data, fetchedAt };
      return {
        ...state,
        [url]: resource,
      };
    }

    case RESOURCE_INVALIDATE: {
      const url = action.payload.url;
      return {
        ...state,
        [url]: undefined,
      };
    }

    case RESOURCE_PATCH_SSE: {
      const { url, id, type, data } = action.payload;
      const newState = { ...state };
      let updated = false;
      for (const [resourceUrl, resource] of Object.entries(newState)) {
        const [resourceBase] = resourceUrl.split('?');

        const detailUrl = `${url}${id}/`;

        const results = (resource?.data as any)?.results as any[];
        if (resourceBase === url && results) {
          const newResourceDataResults = results.map((row) => {
            if (row.id === id) {
              updated = true;
              return { ...row, ...data };
            }
            return row;
          });
          newState[resourceUrl] = {
            ...newState[resourceUrl],
            data: {
              ...newState[resourceUrl].data,
              results: newResourceDataResults,
            },
          };
        } else if (resourceBase === detailUrl) {
          updated = true;

          let newData;
          if (type === SSE_TYPE.CHECKS) {
            const sseData = { ...newState[resourceUrl].data } as DocRetrieveResponse;
            const newChecks = sseData.checks?.map((item) => {
              if (item.id === data.id) {
                item.state = data.state;
              }
              return item;
            });
            newData = {
              checks: newChecks,
            };
          } else {
            newData = { ...data };
          }

          newState[resourceUrl] = {
            ...newState[resourceUrl],
            data: {
              ...newState[resourceUrl].data,
              ...newData,
            },
          };
        }
      }
      return updated ? newState : state;
    }

    default: {
      return state;
    }
  }
}
